'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import { useSearchUsersQuery } from '@/redux/services/portal/admin.api';
import { SearchBox } from '@/components/index.jsx';
import './AddScopeModal.scss';

const AddUserToProjectModal = ({
	open,
	onCancel,
	onConfirm,
	pageSize,
	getUserEmail,
	getUserFirstName,
	getUserLastName,
	existingUserEmails,
	isSubmitting,
}) => {
	const [searchText, setSearchText] = useState('');
	const [selectedUsers, setSelectedUsers] = useState(new Map());

	useEffect(() => {
		if (open) {
			setSearchText('');
			setSelectedUsers(new Map());
		}
	}, [open]);

	const shouldSearch = open && searchText.trim().length >= 1;
	const searchQuery = shouldSearch ? searchText.toUpperCase() : '';
	const searchResult = useSearchUsersQuery(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearch }
	);

	const results = useMemo(() => {
		const items = searchResult.data || [];
		const mapped = items.map((item) => ({
			...item,
			email: getUserEmail(item),
			firstName: getUserFirstName(item),
			lastName: getUserLastName(item),
		}));
		const excludedEmails = new Set([
			...existingUserEmails,
			...Array.from(selectedUsers.keys()),
		]);
		return mapped.filter((item) => {
			const email = getUserEmail(item);
			return email && !excludedEmails.has(email);
		});
	}, [searchResult.data, getUserEmail, getUserFirstName, getUserLastName, existingUserEmails, selectedUsers]);
	const selectedItems = useMemo(() => Array.from(selectedUsers.values()), [selectedUsers]);

	const handleToggleUser = (user) => {
		const email = getUserEmail(user);
		if (!email) return;
		setSelectedUsers((prev) => {
			const next = new Map(prev);
			if (next.has(email)) {
				next.delete(email);
			} else {
				next.set(email, user);
			}
			return next;
		});
	};

	const handleConfirm = () => {
		if (selectedUsers.size === 0 || isSubmitting) return;
		onConfirm(Array.from(selectedUsers.values()));
	};

	return (
		<Box className="AddScopeModal" sx={{ mt: 1 }}>
			<SearchBox
				iconColor="tertiary"
				showResults={false}
				hintText="Search users"
				autoComplete="off"
				name="add-user-project-search"
				disabled={isSubmitting}
				onChange={(value) => setSearchText(value)}
				handleCancel={() => setSearchText('')}
				mapData={{ primaryText: 'name', value: 'id' }}
				dataSource={[]}
			/>
			{!shouldSearch ? (
				<Typography variant="body2" sx={{ mt: 2 }}>
					Start typing to search for a user.
				</Typography>
			) : null}
			{searchResult.isFetching ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={20} />
				</Box>
			) : null}
			{selectedItems.length > 0 ? (
				<>
					<Typography variant="subtitle2" sx={{ mt: 2 }}>
						Selected Users
					</Typography>
					<List dense className="AddScopeModalList">
						{selectedItems.map((user) => (
							<ListItem
								button
								key={getUserEmail(user)}
								selected
							onClick={() => {
								if (!isSubmitting) {
									handleToggleUser(user);
								}
							}}
							>
								<ListItemIcon>
									<Checkbox
										edge="start"
										checked
										tabIndex={-1}
										disableRipple
									disabled={isSubmitting}
									/>
								</ListItemIcon>
								<ListItemText
									primary={`${getUserFirstName(user)} ${getUserLastName(user)}`.trim() || getUserEmail(user)}
									secondary={getUserEmail(user)}
								/>
							</ListItem>
						))}
					</List>
				</>
			) : null}
			{shouldSearch && !searchResult.isFetching ? (
				<List dense className="AddScopeModalList">
					{results.map((user) => (
						<ListItem
							button
							key={getUserEmail(user)}
							selected={selectedUsers.has(getUserEmail(user))}
							onClick={() => {
								if (!isSubmitting) {
									handleToggleUser(user);
								}
							}}
						>
							<ListItemIcon>
								<Checkbox
									edge="start"
									checked={selectedUsers.has(getUserEmail(user))}
									tabIndex={-1}
									disableRipple
									disabled={isSubmitting}
								/>
							</ListItemIcon>
							<ListItemText
								primary={`${getUserFirstName(user)} ${getUserLastName(user)}`.trim() || getUserEmail(user)}
								secondary={getUserEmail(user)}
							/>
						</ListItem>
					))}
					{results.length === 0 ? (
						<ListItem>
							<ListItemText primary="No users found" />
						</ListItem>
					) : null}
				</List>
			) : null}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
				<Button onClick={onCancel} sx={{ mr: 1 }} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleConfirm}
					disabled={selectedUsers.size === 0 || isSubmitting}
				>
					{isSubmitting ? 'Saving...' : `Add Selected (${selectedUsers.size})`}
				</Button>
			</Box>
		</Box>
	);
};

AddUserToProjectModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	getUserEmail: PropTypes.func.isRequired,
	getUserFirstName: PropTypes.func.isRequired,
	getUserLastName: PropTypes.func.isRequired,
	existingUserEmails: PropTypes.arrayOf(PropTypes.string),
	isSubmitting: PropTypes.bool,
};

AddUserToProjectModal.defaultProps = {
	existingUserEmails: [],
	isSubmitting: false,
};

export default AddUserToProjectModal;
